// DDD round-trip — parse a marked-up DDD workbook (the AWS Export-format .xlsx produced by
// dddWorksheet.js) and diff it against the Design Model BY the hidden _id column, mirroring the backend
// lambda/solnstudio/excel/roundTrip.js algorithm. Robust to row reordering. Outcomes:
//   - a changed writable cell            → autoApply (op:'replace') — deterministic
//   - a model element whose _id row was deleted from the sheet → removal (op:'remove') — deterministic
//   - blank _id row with content         → add candidate (engineer-driven, folded in by the next Refine)
//   - _id matching no element            → unmapped (surfaced, never silently dropped)
//
// The Status / Notes review columns were removed (engineer feedback): the engineer now edits the
// document the natural way — DELETE a row to remove the resource, ADD a row to add one. The deleted-row
// sweep below is what makes deletions round-trip without a Status column. Runs client-side: the
// deterministic ops (autoApply + removals) are POSTed to /projects/{id}/design-model/apply-edits (a
// guarded write); adds are shown to the engineer.

import * as XLSX from 'xlsx';
import { COLUMN_MAPPINGS, DATA_START_ROW, SHEET_NAMES, getColumnLetter } from '../../excel/constants';

function colIndex(letter) {
  let n = 0;
  for (const ch of letter) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}
function reviewCols(mapping) {
  const last = Math.max(...Object.values(mapping).map(colIndex));
  return { id: getColumnLetter(last + 1) };
}

// The design-editable DDD sheets and which AWS columns map back to which Design-Model field. Read-only
// columns (natural keys, derived Lex structure) are intentionally omitted from `writable` — a customer
// change there surfaces as a Needs-Change note, not a silent overwrite.
const Q = COLUMN_MAPPINGS.QUEUES;
const I = COLUMN_MAPPINGS.LEX_INTENTS;
const L = COLUMN_MAPPINGS.CONNECT_LAMBDA;
const HOP = COLUMN_MAPPINGS.HOURS_OF_OPERATION;
const RP = COLUMN_MAPPINGS.ROUTING_PROFILES;
const AS = COLUMN_MAPPINGS.AGENT_STATUS;
const QC = COLUMN_MAPPINGS.QUICK_CONNECTS;
// NOTE: Security Profiles is a column-oriented permission matrix (not a row list) and has no Import-tool
// mapper, so it is written column-wise by dddWorksheet.writeSecurityProfiles and does NOT round-trip here.
const DDD_SPECS = [
  { sheet: SHEET_NAMES.HOURS_OF_OPERATION, startKey: 'HOURS_OF_OPERATION', collection: 'hoursOfOperation',
    mapping: HOP, writable: [{ col: HOP.DESCRIPTION, field: 'description' }, { col: HOP.TIME_ZONE, field: 'timeZone' }] },
  { sheet: SHEET_NAMES.QUEUES, startKey: 'QUEUES', collection: 'queues', mapping: Q,
    writable: [{ col: Q.NAME, field: 'connectName' }, { col: Q.DESCRIPTION, field: 'description' },
      { col: Q.HOURS_OF_OPERATION, field: 'hoursOfOperation' }] },
  { sheet: SHEET_NAMES.ROUTING_PROFILES, startKey: 'ROUTING_PROFILES', collection: 'routingProfiles',
    mapping: RP, writable: [{ col: RP.DESCRIPTION, field: 'description' }] },
  { sheet: SHEET_NAMES.AGENT_STATUS, startKey: 'AGENT_STATUS', collection: 'agentStatuses', mapping: AS,
    writable: [{ col: AS.DESCRIPTION, field: 'description' }] },
  { sheet: SHEET_NAMES.QUICK_CONNECTS, startKey: 'QUICK_CONNECTS', collection: 'quickConnects', mapping: QC,
    writable: [{ col: QC.DESCRIPTION, field: 'description' }] },
  { sheet: SHEET_NAMES.USER_HIERARCHIES, startKey: 'USER_HIERARCHY_DATA', collection: 'hierarchyGroups',
    mapping: COLUMN_MAPPINGS.USER_HIERARCHY_DATA, writable: [] }, // structure sheet — delete-row removal only
  { sheet: SHEET_NAMES.LEX_INTENTS, startKey: 'LEX_INTENTS', collection: 'intents', mapping: I,
    writable: [{ col: I.DESCRIPTION, field: 'description' }] },
  { sheet: SHEET_NAMES.CONNECT_LAMBDA, startKey: 'CONNECT_LAMBDA', collection: 'lambdas', mapping: L,
    writable: [{ col: L.DESCRIPTION, field: 'description' }] },
  { sheet: SHEET_NAMES.CONTACT_FLOWS, startKey: 'CONTACT_FLOWS', collection: 'flows',
    mapping: COLUMN_MAPPINGS.CONTACT_FLOWS, writable: [] }, // delete-row removal only
];

function norm(v) {
  return String(v == null ? '' : v).trim();
}

/**
 * Parse + diff a DDD workbook against the Design Model.
 * @param {File} file
 * @param {object} model  current Design Model
 * @returns {Promise<{ ops, autoApply, removals, adds, unmapped, summary }>}
 */
export async function diffDddWorkbook(file, model) {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const autoApply = [];
  const removals = [];
  const adds = [];
  const unmapped = [];

  for (const spec of DDD_SPECS) {
    const ws = wb.Sheets[spec.sheet];
    // Only sweep deletions for sheets actually PRESENT in the uploaded workbook — a missing sheet must
    // never be read as "every row deleted".
    if (!ws) continue;
    const rc = reviewCols(spec.mapping);
    const start = DATA_START_ROW[spec.startKey];
    // header:'A' → keys are column letters; range skips the template's header rows.
    const rows = XLSX.utils.sheet_to_json(ws, { header: 'A', range: start - 1, blankrows: false });
    const byId = new Map((model[spec.collection] || []).map((el) => [el.id, el]));
    const seenIds = new Set();

    for (const row of rows) {
      const id = norm(row[rc.id]);

      if (!id) {
        // Blank _id → a row the engineer typed in. Skip the template's empty filler rows (nothing typed).
        const hasContent = spec.writable.some((w) => norm(row[w.col]));
        if (hasContent) adds.push({ collection: spec.collection, sheet: spec.sheet, row });
        continue;
      }
      seenIds.add(id);
      const el = byId.get(id);
      if (!el) { unmapped.push({ sheet: spec.sheet, id }); continue; }
      for (const w of spec.writable) {
        if (!(w.col in row)) continue;
        const uploaded = norm(row[w.col]);
        const current = norm(el[w.field]);
        if (uploaded !== current) {
          autoApply.push({ op: 'replace', path: `/${spec.collection}/${id}/${w.field}`, value: uploaded });
        }
      }
    }

    // Deleted-row sweep — any model element whose _id row is absent from this (present) sheet is a
    // removal. This is what makes "delete the row to delete the resource" round-trip without a Status col.
    for (const [id] of byId) {
      if (!seenIds.has(id)) removals.push({ op: 'remove', path: `/${spec.collection}/${id}` });
    }
  }

  const ops = [...autoApply, ...removals];
  return {
    ops,
    autoApply,
    removals,
    adds,
    unmapped,
    summary: {
      autoApply: autoApply.length,
      removals: removals.length,
      adds: adds.length,
      unmapped: unmapped.length,
    },
  };
}

export default diffDddWorkbook;
